import styled from '@emotion/styled'

import { cardBorderColor } from '../../components/advice/shared'
import { translucentCardBackground } from '../../components/RoundedCard'
import { XSmallSecondaryButton } from '../../components/Button'
import { SectionHeading } from '../dashboards/dashboardStyles'
import { MOBILE } from '../../guided-toolbox/utils/breakpoints'

export const PageContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;

  ${MOBILE} {
    padding: 0 20px;
  }
`

export const PageTitle = styled.h1`
  margin: 0 0 30px;
  color: ${p => p.theme.mediumGray};
  font-size: 50px;
  font-weight: 300;
  line-height: 1.2;

  ${MOBILE} {
    margin-bottom: 20px;
    font-size: 30px;
  }
`

const cardBgColor = p => p.theme.cardBgColor ?? '#ffffff'

export const CardFrame = styled.section`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 900px;
  margin-bottom: 20px;
  padding: 20px;
  border: 2px solid ${cardBorderColor};
  border-radius: 10px;

  background-color: ${cardBgColor};
  ${translucentCardBackground}
`

/**
 * The heading and the edit button share a row on desktop. On mobile the header
 * dissolves so both drop into the card's own column and the button can be
 * ordered below the fields.
 */
export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 15px;

  ${MOBILE} {
    display: contents;
  }
`

export const CardTitle = styled(SectionHeading)`
  ${MOBILE} {
    padding-bottom: 20px;
  }
`

export const CardEditButton = styled(XSmallSecondaryButton)`
  flex: none;
  min-width: 79px;
  padding: 7px 26px;
  font-weight: 400;

  ${MOBILE} {
    order: 1;
    width: 100%;
    margin-top: 20px;
  }
`

/**
 * Groups related fields so they stay visually together. Desktop rows are single
 * lines, so a group only needs a small break; mobile rows stack their label and
 * value, so fields get 10px between them and groups get 30px.
 */
export const FieldGroup = styled.div`
  & + & {
    margin-top: 17px;
  }

  ${MOBILE} {
    display: flex;
    flex-direction: column;
    gap: 10px;

    & + & {
      margin-top: 30px;
    }
  }
`

export const FieldRow = styled.div`
  display: flex;
  align-items: baseline;
  font-size: 16px;
  line-height: 22px;

  ${MOBILE} {
    display: block;
  }
`

export const FieldLabel = styled.div`
  flex: 1 1 400px;
  min-width: 0;
  padding-right: 8px;
  color: ${p => p.theme.cardFieldLabelColor};
  text-align: right;
  overflow-wrap: break-word;

  ${MOBILE} {
    padding-right: 0;
    text-align: left;
  }
`

export const FieldValue = styled.div`
  flex: 1 1 400px;
  min-width: 0;
  padding-left: 8px;
  color: ${p => p.theme.cardFieldValueColor};
  overflow-wrap: break-word;

  ${MOBILE} {
    padding-left: 0;

    /* Stacked values sit under their label, so a field with nothing on record
       still has to hold its line. Without this it collapses to half height and
       the even spacing between the fields around it reads as uneven. */
    min-height: 22px;
  }
`

/**
 * Holds the value column open for a heading, which has no value of its own.
 * Without it the label is the only item in the row and grows across the full
 * width, throwing the right-aligned heading out of the label column. Dropped on
 * mobile, where the columns stack and it would reserve a blank line instead.
 */
export const FieldValueSpacer = styled(FieldValue)`
  ${MOBILE} {
    display: none;
  }
`
